/**
 * Science page sub navigation: hide on scroll down, show on scroll up.
 * Smooth-scrolls to section anchors from subnav, mega menu, or URL hash.
 * Scoped to the science page (script is only loaded on page.science).
 */
(function () {
  const SECTION_IDS = [
    "science-overview",
    "science-clinical-research",
    "science-featured-articles",
  ];

  const nav = document.querySelector(".science-page-subnav");

  function getSectionIdFromHref(href) {
    if (!href) return null;

    try {
      const url = new URL(href, window.location.origin);
      let id = url.hash ? url.hash.slice(1) : "";
      try {
        id = decodeURIComponent(id);
      } catch (_) {
        /* keep raw */
      }
      return SECTION_IDS.includes(id) ? id : null;
    } catch (_) {
      return null;
    }
  }

  function isSamePageLink(href) {
    try {
      const url = new URL(href, window.location.origin);
      const normalize = (path) => path.replace(/\/+$/, "") || "/";
      return normalize(url.pathname) === normalize(window.location.pathname);
    } catch (_) {
      return href.charAt(0) === "#";
    }
  }

  function closeDesktopDropdown() {
    document.body.classList.remove("dropdown-open-desk");
    document.querySelectorAll(".dropdown-opened").forEach((el) => {
      el.classList.remove("dropdown-opened");
    });
  }

  function closeMobileNavDrawer() {
    const drawer =
      document.querySelector("drawer-nav#wt-drawer-nav") ||
      document.querySelector("drawer-nav");

    if (drawer && typeof drawer.closeNavDrawerSafe === "function") {
      drawer.closeNavDrawerSafe();
      return;
    }

    document.body.classList.remove("menu-open", "menu-drawer-overlay-on");
    document.querySelectorAll(".submenu-opened").forEach((el) => {
      el.classList.remove("submenu-opened");
      const toggle = el.querySelector(".wt-page-nav-mega__submenu-toggle");
      if (toggle) toggle.setAttribute("aria-expanded", "false");
    });
  }

  function closeOpenNavigation() {
    closeDesktopDropdown();
    closeMobileNavDrawer();
  }

  function scrollToSection(id, updateHash) {
    const target = document.getElementById(id);
    if (!target) return false;

    if (typeof smoothScrollToElement === "function") {
      smoothScrollToElement(target);
    } else {
      try {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch (_) {
        target.scrollIntoView(true);
      }
    }

    if (updateHash && history.replaceState) {
      history.replaceState(null, "", "#" + id);
    }

    markActiveSectionLink(id);
    return true;
  }

  function markActiveSectionLink(id) {
    document
      .querySelectorAll(
        ".wt-page-nav-mega__sublist__link.is-section-active, .science-page-subnav__link.is-section-active",
      )
      .forEach((el) => el.classList.remove("is-section-active"));

    // Clear section-driven parent active marks (keep Shopify link.current ones from Liquid).
    document
      .querySelectorAll(".is-active-nav[data-section-active]")
      .forEach((el) => {
        el.classList.remove("is-active-nav");
        el.removeAttribute("data-section-active");
      });

    if (!id) return;

    document.querySelectorAll("a[href]").forEach((link) => {
      const href = link.getAttribute("href");
      if (getSectionIdFromHref(href) !== id) return;

      if (
        link.classList.contains("wt-page-nav-mega__sublist__link") ||
        link.classList.contains("science-page-subnav__link")
      ) {
        link.classList.add("is-section-active");
      }

      // Mark parent dropdown row(s) so label + plus/minus stay white.
      const nestedParent = link.closest(".wt-page-nav-mega__sublist--parent");
      const topParent = link.closest(".wt-page-nav-mega__item--parent");

      [nestedParent, topParent].forEach((parentRow) => {
        if (!parentRow) return;
        if (!parentRow.classList.contains("is-active-nav")) {
          parentRow.classList.add("is-active-nav");
          parentRow.setAttribute("data-section-active", "true");
        }
      });
    });
  }

  function onAnchorClick(event) {
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    // Ignore plus/minus toggle — submenu only.
    if (event.target.closest(".wt-page-nav-mega__submenu-toggle")) return;

    // Drawer capture handler owns in-drawer link activation on real devices.
    if (
      event.target.closest("drawer-nav") &&
      document.body.classList.contains("menu-open")
    ) {
      return;
    }

    const link = event.target.closest("a[href]");
    if (!link) return;

    const href = link.getAttribute("href");
    const id = getSectionIdFromHref(href);
    if (!id || !isSamePageLink(href)) return;
    if (!document.getElementById(id)) return;

    event.preventDefault();
    closeOpenNavigation();

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        scrollToSection(id, true);
      }, 40);
    });
  }

  // Cross-page landing: early head script stores __scienceScrollTo and strips the hash.
  const pendingId =
    window.__scienceScrollTo ||
    (window.location.hash ? window.location.hash.slice(1) : "");

  function runPendingSmoothScroll(id) {
    if (!SECTION_IDS.includes(id) || !document.getElementById(id)) return;

    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    window.scrollTo(0, 0);

    const start = () => {
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          scrollToSection(id, true);
          try {
            delete window.__scienceScrollTo;
          } catch (_) {
            window.__scienceScrollTo = null;
          }
        }, 150);
      });
    };

    if (document.readyState === "complete") {
      start();
    } else {
      window.addEventListener("load", start, { once: true });
    }
  }

  if (pendingId) {
    runPendingSmoothScroll(pendingId);
  } else if (window.location.hash) {
    markActiveSectionLink(window.location.hash.slice(1));
  }

  // click covers mouse + synthesized click after touch on iOS/Android.
  document.addEventListener("click", onAnchorClick);

  window.addEventListener("hashchange", () => {
    markActiveSectionLink(
      window.location.hash ? window.location.hash.slice(1) : "",
    );
  });

  // Subnav hide / show on scroll (only when the bar exists).
  if (!nav) return;

  nav.classList.remove("is-hidden");

  let lastY = window.scrollY || 0;
  let latestY = lastY;
  let rafScheduled = false;

  const HIDE_DELTA = 12;
  const SHOW_DELTA = 8;
  const TOP_SHOW_Y = 10;

  function update() {
    rafScheduled = false;
    const y = latestY;
    const delta = y - lastY;

    if (y <= TOP_SHOW_Y) {
      nav.classList.remove("is-hidden");
      lastY = y;
      return;
    }

    if (delta > HIDE_DELTA) {
      nav.classList.add("is-hidden");
    } else if (delta < -SHOW_DELTA) {
      nav.classList.remove("is-hidden");
    }

    lastY = y;
  }

  function onScroll() {
    latestY = window.scrollY || 0;
    if (rafScheduled) return;

    rafScheduled = true;
    window.requestAnimationFrame(update);
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", () => {
    lastY = window.scrollY || 0;
    latestY = lastY;
  });
})();
